// frontend/src/utils/DateUtils.js
/**
 * Date utility functions to handle date formatting without timezone issues.
 * Fixes the off-by-one bug when displaying dates.
 */

/**
 * Format a date string for input fields (YYYY-MM-DD format)
 * Fixes timezone offset issues by parsing the date correctly
 * 
 * @param {string} dateString - Date in YYYY-MM-DD or ISO format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  
  // If already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // Handle ISO format (YYYY-MM-DDTHH:mm:ss.sssZ)
  const parts = dateString.split("T")[0].split("-");
  if (parts.length === 3) {
    return dateString.split("T")[0];
  }
  
  return dateString;
};

/**
 * Format a date string for display
 * Uses local date parsing to avoid timezone issues
 * 
 * @param {string} dateString - Date string to format
 * @param {string} format - Format string (e.g., "MMM DD", "MMM DD, YYYY")
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, format = "MMM DD") => {
  if (!dateString) return "—";
  
  // Parse date without timezone conversion
  // This fixes the off-by-one issue
  const [year, month, day] = dateString.split("T")[0].split("-").map(Number);
  const d = new Date(year, month - 1, day); // month is 0-indexed
  
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];
  
  const monthName = monthNames[d.getMonth()];
  const dayNum = d.getDate();
  
  if (format === "MMM DD") {
    return `${monthName} ${dayNum}`;
  } else if (format === "MMM DD, YYYY") {
    return `${monthName} ${dayNum}, ${d.getFullYear()}`;
  } else if (format === "YYYY-MM-DD") {
    return dateString.split("T")[0];
  } else if (format === "YYYY") {
    return String(d.getFullYear());
  }
  
  return `${monthName} ${dayNum}`;
};

/**
 * Get current date in YYYY-MM-DD format
 * @returns {string}
 */
export const getTodayString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a date string without timezone conversion
 * @param {string} dateString 
 * @returns {Date}
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("T")[0].split("-").map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Check if a date is within N months of another date
 * @param {string} dateString - Date to check
 * @param {number} months - Number of months
 * @returns {boolean}
 */
export const isWithinMonths = (dateString, months) => {
  if (!dateString) return false;
  
  const targetDate = parseLocalDate(dateString);
  const today = new Date();
  const monthsDiff = (targetDate.getFullYear() - today.getFullYear()) * 12 + 
                     (targetDate.getMonth() - today.getMonth());
  
  return monthsDiff >= 0 && monthsDiff <= months;
};

/**
 * Calculate months until a date
 * @param {string} dateString 
 * @returns {number} Months until date (negative if in past)
 */
export const monthsUntil = (dateString) => {
  if (!dateString) return null;
  
  const targetDate = parseLocalDate(dateString);
  const today = new Date();
  
  return (targetDate.getFullYear() - today.getFullYear()) * 12 + 
         (targetDate.getMonth() - today.getMonth());
};

/**
 * Format fiscal year from date
 * @param {string} dateString 
 * @returns {string} Fiscal year (YYYY)
 */
export const getFiscalYear = (dateString) => {
  if (!dateString) return "";
  return formatDate(dateString, "YYYY");
};
